import { useEffect, type MouseEvent, type ReactNode } from "react";
import { useFetcher } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ErrorNote } from "@/components/form";
import { useWideScreen } from "@/hooks/use-media-query";
import { bringsUpKeyboard } from "@/hooks/use-typing";

/**
 * A sheet over the page (decided with Tiago 16 October): shadcn's Sheet
 * rising from the bottom on a phone, its Dialog in the middle of a wider
 * screen. Open while it is drawn — a caller shows it, and `onClose` is how
 * it asks to go: Escape, the backdrop, or its close button. Focus moves into
 * it and back, and the page behind neither scrolls nor takes a tap.
 *
 * On a phone a sheet is as tall as what it holds. While a box in it is typed
 * in, it takes the screen's whole height, which carries the box up clear of
 * the keyboard, with a keyboard's height of room after its last line, so that
 * a box further down is scrolled into sight within the sheet rather than
 * Safari sliding the page up (index.css, `data-keyboard-sheet`).
 */
export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const wide = useWideScreen();
  const close = (open: boolean) => {
    if (!open) onClose();
  };
  if (wide) {
    return (
      <Dialog open onOpenChange={close}>
        <DialogContent aria-describedby={undefined} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">{children}</div>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open onOpenChange={close}>
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        data-keyboard-sheet=""
        className="max-h-[90vh] overflow-y-auto rounded-t-3xl"
        onMouseDown={keepTyping}
      >
        <SheetHeader className="pr-14">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">{children}</div>
        <div aria-hidden="true" data-keyboard-room="" />
      </SheetContent>
    </Sheet>
  );
}

/** A box or a picker: a press on one takes the focus, as it must. */
const CONTROL = "input, textarea, select, [contenteditable]";

/**
 * Pressing anything but a box or a picker, while a box is typed in on a touch
 * screen, leaves the focus where it is: let go, the sheet would shrink under
 * the finger, and the press would miss what it was aimed at — its close
 * button, its action. The keyboard stays up, as a phone app's does, until the
 * sheet closes. Typing is the bars' own test (ADR 0071), and only presses on
 * the sheet itself count, not on a menu or a sheet drawn from inside it.
 */
function keepTyping(event: MouseEvent<HTMLDivElement>) {
  const target = event.target;
  if (!(target instanceof Element) || !event.currentTarget.contains(target)) return;
  if (!window.matchMedia?.("(pointer: coarse)").matches) return;
  if (bringsUpKeyboard(document.activeElement) && !target.closest(CONTROL)) event.preventDefault();
}

/** What a sheet's action says back: done, or why not — and the box it is about, when there is one. */
export type Outcome = { ok?: string; error?: string; field?: string };

/** Closes a dialog once its fetcher has answered without an error. */
export function useClosedWhenDone(fetcher: { state: string; data?: Outcome }, onClose: () => void) {
  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data && !fetcher.data.error) onClose();
  }, [fetcher.state, fetcher.data, onClose]);
}

/**
 * Asked before letting go of something typed and not sent — leaving a form,
 * or cancelling one (decided with Tiago 20 October: never the browser's own
 * `confirm`). What goes, Keep editing, and a red button saying what pressing
 * it does; nothing is posted, `onConfirm` does what was asked.
 */
export function DiscardDialog({
  title,
  confirm,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  /** The red button's words: "Leave", "Drop it". */
  confirm: string;
  onConfirm: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <div className="text-sm text-muted-foreground">{children}</div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="lg" onClick={onClose}>
          Keep editing
        </Button>
        <Button type="button" variant="destructive" size="lg" onClick={onConfirm}>
          {confirm}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Something that cannot be undone, asked first: what it does, Cancel, and a
 * red button. It posts `fields` to the page's own action by fetcher, shows
 * the action's error in place, and closes once it answers without one.
 */
export function ConfirmDialog({
  title,
  fields,
  confirm,
  onClose,
  children,
}: {
  title: string;
  /** What the form sends: the action's `intent` and whatever names its target. */
  fields: Record<string, string>;
  /** The red button's words: what pressing it does. */
  confirm: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const fetcher = useFetcher<Outcome>();
  useClosedWhenDone(fetcher, onClose);
  const busy = fetcher.state !== "idle";
  return (
    <Modal title={title} onClose={onClose}>
      <fetcher.Form method="post" className="space-y-4">
        {Object.entries(fields).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
        <div className="text-sm text-muted-foreground">{children}</div>
        {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" size="lg" disabled={busy}>
            {confirm}
          </Button>
        </div>
      </fetcher.Form>
    </Modal>
  );
}
